import { gql } from "graphql-request";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { setupExperiments } from "hooks/useExperiments/utils";
import { useIsOptimizelyEnabled, MAX_RETRIES_OPTIMIZELY, RETRIES_INTERVAL } from "hooks/useIsOptimizelyEnabled";
import { ExperimentsListContent } from "types/graphql/generated";

/**
 * Custom hook for managing experiments.
 * @param experimentsList - The list of experiments to be setup.
 * @param enabled - Flag indicating whether experiments are enabled or not.
 * @returns An object containing the setup status and activation status of the experiments.
 */

export const useExperiment = (experimentsList: ExperimentsListContent[], enabled: boolean) => {
  const router = useRouter();
  const isOptimizelyActive = useIsOptimizelyEnabled();
  const [isSetupComplete, setIsSetupComplete] = useState(!experimentsList || !enabled);
  const [isActive, setIsActive] = useState(!experimentsList || !enabled);

  const handleExperimentComplete = () => {
    setIsSetupComplete(true);
  };

  const handleRouteChangeStart = useCallback(() => {
    if (!experimentsList || !enabled || !isOptimizelyActive) return;
    setIsSetupComplete(false);
  }, [experimentsList, enabled, isOptimizelyActive]);

  // If we can't setup the experiments, we can render the page
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isOptimizelyActive || isSetupComplete) return;
      handleExperimentComplete();
    }, MAX_RETRIES_OPTIMIZELY * RETRIES_INTERVAL);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isOptimizelyActive || !enabled) return;

    const isActive = setupExperiments(experimentsList);

    setIsActive(isActive);

    // If we don't have any active experiments, we can render the page
    if (!isActive) {
      handleExperimentComplete();
    }
  }, [enabled, experimentsList, isOptimizelyActive]);

  // Redirect experiments are handled by the router
  useEffect(() => {
    router.events.on("routeChangeComplete", handleExperimentComplete);

    return () => router.events.off("routeChangeComplete", handleExperimentComplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    router.events.on("routeChangeStart", handleRouteChangeStart);

    return () => router.events.off("routeChangeStart", handleRouteChangeStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isReady: isSetupComplete, isActive };
};

gql`
  fragment ExperimentsContent on ComponentExperimentsExperiments {
    enabled
    experimentsList {
      ...ExperimentsListContent
    }
  }
`;

gql`
  fragment ExperimentsListContent on ComponentNestedExperimentsOption {
    experiment_id
    variant_id
    label
    type
    url
    enabled
  }
`;
